import { Aggregator } from "./aggregator";
import { JmApi } from "../jm-api/jm-api";
import { Rule, MetricType, CompareRule } from "./rule";

export enum RuleType {
  Min,
  Max,
}

export class MetricsManager {
  public static BASE_URL =
    import.meta.env.VITE_CLEVERREACH_API_BASE_URL + "/jmapi/v1/metrics";

  static getUrlBuilder(
    projectId: number,
    companyId: number,
    officeId: number,
    transport: string | undefined,
    metricType: MetricType | undefined = undefined,
    withSurcharge = false,
  ): string {
    let url = `${MetricsManager.BASE_URL}?projId=${projectId}&CompanyId=${companyId}&officeId=${officeId}`;

    if (transport) {
      url += `&transport=${encodeURIComponent(transport)}`;
    }

    if (metricType) {
      url += `&metricType=${encodeURIComponent(metricType)}`;
    }

    // Stauaufschlag: wirkt serverseitig ausschliesslich auf die Zeitwerte.
    // Nur anhaengen, wenn er auch gewuenscht ist - so bleibt die URL (und
    // damit jeder Cache davor) fuer den Normalfall unveraendert.
    if (withSurcharge) {
      url += `&withSurcharge=true`;
    }

    return url;
  }

  static async get(
    projectId: number,
    companyId: number,
    officeId: number,
    transport: string | undefined,
    metricType: string | undefined = undefined,
    withSurcharge = false,
  ): Promise<object> {
    try {
      const url: string = MetricsManager.getUrlBuilder(
        projectId,
        companyId,
        officeId,
        transport,
        metricType,
        withSurcharge,
      );
      // console.log("MetricsManager.get URL:", url);

      const response = await JmApi.get(url);
      // console.log("MetricsManager.get raw response:", response);

      // Check if response is empty or undefined
      if (!response || Object.keys(response).length === 0) {
        throw new Error("No metrics data received from the server");
      }

      return response;
    } catch (error) {
      console.error("MetricsManager.get error:", error);
      throw error;
    }
  }

  static async condGet(
    projectId: number,
    companyId: number,
    officeId: number,
    rule: Rule | CompareRule,
  ): Promise<object> {
    // first handle if rule of type CompareRule
    let t1Data: object | undefined = undefined;
    let t2Data: object | undefined = undefined;

    if ((rule as CompareRule).transportType2) {
      await MetricsManager.get(
        projectId,
        companyId,
        officeId,
        rule.transportType,
      )
        .then((data) => {
          t1Data = data;
        })
        .catch((message) => {
          throw new Error(message);
        });
      await MetricsManager.get(
        projectId,
        companyId,
        officeId,
        (rule as CompareRule).transportType2,
      )
        .then((data) => {
          t2Data = data;
        })
        .catch((message) => {
          throw new Error(message);
        });
    }

    return new Promise((resolve, reject) => {
      if (!(rule as CompareRule).transportType2) {
        MetricsManager.get(projectId, companyId, officeId, rule.transportType)
          .then((data) => {
            const result = MetricsManager.resolveRule(rule, data);
            resolve(result);
          })
          .catch((message) => {
            reject(message);
          });
      } else if (t1Data && t2Data) {
        // dealing with comparerule
        const comparison: object = {};

        // compute comparison value
        for (const key in t1Data) {
          // @ts-ignore
          if (t2Data[key]) {
            // @ts-ignore
            comparison[key] = (rule as CompareRule).compute(
              // @ts-ignore
              t1Data[key][rule.metricType],
              // @ts-ignore
              t2Data[key][rule.metricType],
            );
          }
        }
        let sliced: object = {};

        // then apply the rule-filter to the computed values
        if (rule.ruleType === RuleType.Max) {
          sliced = Aggregator.maxSlice(comparison, rule.tHold);
        } else {
          sliced = Aggregator.minSlice(comparison, rule.tHold);
        }

        // then filter the actual data
        const result: object = {};
        for (const key in sliced) {
          // @ts-ignore
          result[key] = t1Data[key];
        }
        resolve(result);
      } else {
        throw new Error(
          "no successful get operation for compare Rule performed",
        );
      }
    });
  }

  static resolveCompareRule(rule: CompareRule, data1: object, data2: object) {
    const comparison: object = {};
    for (const key in data1) {
      // @ts-ignore
      if (data2[key]) {
        // @ts-ignore
        comparison[key] = rule.compute(
          // @ts-ignore
          data1[key][rule.metricType],
          // @ts-ignore
          data2[key][rule.metricType],
        );
      }
    }
    let sliced: object;

    // then apply the rule-filter to the computed values
    if (rule.ruleType === RuleType.Max) {
      sliced = Aggregator.maxSlice(comparison, rule.tHold);
    } else {
      sliced = Aggregator.minSlice(comparison, rule.tHold);
    }
    return sliced;
  }

  static resolveRule(rule: Rule, data: object) {
    const toSlice = {};

    // this runtime will be not good, but we will take care of it
    // isolate the data
    for (const key in data) {
      // @ts-ignore
      if (data[key][rule.metricType]) {
        // @ts-ignore
        toSlice[key] = data[key][rule.metricType];
      }
    }

    let sliced: object = {};
    // ...then slice the data
    if (rule.ruleType === RuleType.Max) {
      sliced = Aggregator.maxSlice(toSlice, rule.tHold);
    } else {
      sliced = Aggregator.minSlice(toSlice, rule.tHold);
    }

    // ...then filter given data
    const result: object = {};
    for (const key in sliced) {
      // @ts-ignore
      result[key] = data[key];
    }

    return result;
  }
}
